// src/contexts/AuthContext.tsx
import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface User {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
    role: 'admin' | 'user';
    is_admin?: boolean;
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    login: (email: string, password: string) => Promise<void>;
    logout: () => void;
    isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null)
    const [token, setToken] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        console.log('AuthContext: Checking initial auth state')
        checkAuthState()
    }, [])

    const checkAuthState = async () => {
        const savedToken = localStorage.getItem('token')
        if (savedToken) {
            console.log('AuthContext: Found token, verifying...')
            setToken(savedToken)
            await verifyToken(savedToken)
        } else {
            console.log('AuthContext: No token found')
            setIsLoading(false)
        }
    }

    const verifyToken = async (token: string) => {
        try {
            console.log('AuthContext: Starting token verification')
            console.log('AuthContext: Token prefix:', token.substring(0, 10) + '...')
            
            const response = await fetch('/api/auth/verify', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })
            
            console.log('AuthContext: Verify response status:', response.status)
            
            if (response.ok) {
                const userData = await response.json()
                console.log('AuthContext: Token verified, user data:', userData)
                
                // Transform the user data to ensure role is set
                const userWithRole = {
                    ...userData,
                    role: userData.role || (userData.is_admin ? 'admin' : 'user')
                }
                
                console.log('AuthContext: Setting user with role:', userWithRole)
                
                setUser(userWithRole)
                
                // Test prescription endpoint with verified token
                const prescriptionTest = await fetch('/api/prescriptions', {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                })
                console.log('AuthContext: Prescription test response:', prescriptionTest.status)
                if (prescriptionTest.ok) {
                    const prescriptionData = await prescriptionTest.json()
                    console.log('AuthContext: Prescription data available:', {
                        count: prescriptionData.length,
                        firstGroup: prescriptionData[0]?.id
                    })
                }
            } else {
                console.log('AuthContext: Token invalid')
                localStorage.removeItem('token')
                setUser(null)
                setToken(null)
            }
        } catch (error) {
            console.error('AuthContext: Token verification failed:', error)
            localStorage.removeItem('token')
            setUser(null)
            setToken(null)
        } finally {
            setIsLoading(false)
        }
    }

    const login = async (email: string, password: string) => {
        try {
            console.log('AuthContext: Starting login process')
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
            })
            
            console.log('AuthContext: Login response status:', response.status)
            
            if (!response.ok) {
                // Handle rate limiting (HTTP 429)
                if (response.status === 429) {
                    console.error('AuthContext: Rate limit exceeded')
                    throw new Error('Too many login attempts. Please wait a moment and try again.')
                }
                
                // For other errors, try to parse the JSON
                try {
                    const errorData = await response.json()
                    console.error('AuthContext: Login failed:', errorData)
                    throw new Error(errorData.message || 'Login failed')
                } catch (jsonError) {
                    // If we can't parse JSON, use the response status text
                    console.error('AuthContext: Could not parse error response:', jsonError)
                    throw new Error(`Login failed: ${response.statusText || 'Unknown error'}`)
                }
            }

            const data = await response.json()
            console.log('AuthContext: Login successful, data:', {
                hasToken: !!data.token,
                hasUser: !!data.user,
                userData: data.user
            })

            localStorage.setItem('token', data.token)
            setToken(data.token)
            
            // Transform the user data to ensure role is set
            const userWithRole = {
                ...data.user,
                role: data.user.role || (data.user.is_admin ? 'admin' : 'user')
            }
            
            console.log('AuthContext: Setting user with role:', userWithRole)
            setUser(userWithRole)
            
            // Test prescription endpoint immediately after login
            const prescriptionTest = await fetch('/api/prescriptions', {
                headers: {
                    'Authorization': `Bearer ${data.token}`
                }
            })
            console.log('AuthContext: Post-login prescription test:', {
                status: prescriptionTest.status,
                ok: prescriptionTest.ok
            })
            
            // Force a recheck of auth state
            await checkAuthState()
        } catch (error) {
            console.error('AuthContext: Login error:', error)
            throw error
        }
    }

    const logout = () => {
        console.log('AuthContext: Logging out')
        localStorage.removeItem('token')
        setUser(null)
        setToken(null)
    }

    const value = {
        user,
        token,
        login,
        logout,
        isLoading
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}